import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';

import { Col, Container, Row } from 'react-bootstrap';

import styles from './SelectedBreedingDog.module.scss';
import Card from '../../Shared/Card/Card';
import { general } from '../../../general';
import PedigreeCard from '../Statistics/Cards/PedigreeCard/PedigreeCard';
import { usePedigree } from '../Statistics/Cards/PedigreeCard/usePedigree';

function SelectedBreedingDog(props: SelectedBreedingDogProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();
    const location = useLocation();

    // Listesidas query-streng (fanen, eller åpen seksjon på /admin) følger med hit
    // i history state, slik at knappene under leverer brukeren tilbake til det de
    // kom fra og ikke til standardvisningen. Åpnet man detaljsiden via en direkte
    // lenke finnes ingen state, og vi lander på listesida uendret.
    const listUrl = "/avlshundsoknader" + ((location.state as any)?.listSearch ?? "");

    const [selectedReg, setSelectedReg] = useState<any | undefined>();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [familyTreeGenerations, setFamilyTreeGenerations] = useState<number>(2);

    const { data: matchedDog } = useQuery({
        queryKey: ['dog', selectedReg?.DOGMATCH?.ANIMAL_ID],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/dogs/" + selectedReg?.DOGMATCH?.ANIMAL_ID,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: !!selectedReg?.DOGMATCH?.ANIMAL_ID,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    function getBreedingDog() {
        axios.single({
            method: 'get',
            url: "/api/schema/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function acceptBreeding() {
        axios.single({
            method: 'post',
            url: "/api/schema" + "/accept/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function rejectBreeding() {
        axios.single({
            method: 'post',
            url: "/api/schema" + "/reject/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function deleteBreeding() {
        axios.single({
            method: 'delete',
            url: "/api/schema" + "/delete/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function searchDog() {
        if (!searchTerm || searchTerm.trim() === "") {
            return;
        }
        
        setIsSearching(true);
        axios.single({
            method: 'post',
            url: "/api/dogs/searchdogs",
            data: {
                breedid: auth.selectedBreed,
                search: searchTerm
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSearchResults(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsSearching(false);
        });
    }

    function selectDog(dog: any) {
        axios.single({
            method: 'post',
            url: "/api/schema/updateDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            data: {
                animalId: dog.ANIMAL_ID,
                regnumber: dog.REGNO
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
            setSearchResults([]);
            setSearchTerm("");
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function removeDogMatch() {
        axios.single({
            method: 'post',
            url: "/api/schema/removeDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }
        
    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    useEffect(() => {
        if (params.breedregid != undefined && auth.selectedBreed != undefined && auth.selectedBreed != "") {
            getBreedingDog();
        }
    }, [params.breedregid, auth.selectedBreed]);

    const pedigree = usePedigree(matchedDog?.ANIMAL_ID, familyTreeGenerations);
    const matchedIsMale = matchedDog?.SEX === "male";

    return (
        <div className="maincontent">
            <div>
                <h1>{t("selectedbreedingdog.title")}</h1>
                <hr/>
                
                <div className={styles.actions}>
                    <button className="btn btn-primary" onClick={() => navigate(listUrl)}>
                        {t("selectedbreedingdog.buttons.back")}
                    </button>
                    <button className="btn btn-primary btn-negative" onClick={() => {
                        rejectBreeding();
                    }}>
                        {t("selectedbreedingdog.buttons.reject")}
                    </button>
                    <button
                        className="btn btn-primary btn-positive"
                        disabled={selectedReg == undefined || selectedReg.DOGMATCH == undefined || selectedReg.DOGMATCH.REGNO == undefined}
                        onClick={() => {
                            if (selectedReg != undefined && selectedReg.DOGMATCH != undefined && selectedReg.DOGMATCH.REGNO != undefined) {
                                acceptBreeding();
                            }
                        }}
                    >
                        {t("selectedbreedingdog.buttons.accept")}
                    </button>
                    <button
                        className={"btn btn-primary btn-negative " + styles.deletebutton}
                        onClick={() => {
                            window.confirm(t("selectedbreedingdog.deleteconfirm")) &&
                                deleteBreeding();
                        }}
                    >
                        {t("selectedbreedingdog.buttons.delete")}
                    </button>
                </div>
    
                <hr/>
                <Container className={styles.container}>
                    <Row className={styles.row}>
                        {matchedDog?.ANIMAL_ID != undefined && (
                            <Col xs="12" className={styles.col}>
                                <Card
                                    title={t("selectedopenbreeding.familytree.title")}
                                    children={<div>
                                        <PedigreeCard
                                            dam={matchedIsMale ? undefined : pedigree.data}
                                            sire={matchedIsMale ? pedigree.data : undefined}
                                            damId={matchedIsMale ? undefined : matchedDog?.ANIMAL_ID}
                                            sireId={matchedIsMale ? matchedDog?.ANIMAL_ID : undefined}
                                            loading={pedigree.loading}
                                            generations={familyTreeGenerations}
                                            onGenerationsChange={setFamilyTreeGenerations}
                                        />
                                    </div>}
                                ></Card>
                            </Col>
                        )}
                        <Col
                            sm="12"
                            md="12"
                            lg="6"
                            xl="6"
                            xxl="6"
                            className={styles.col}
                        >
                            <Card
                                title={t("selectedbreedingdog.results.title")}
                                children={<div>
                                    <hr/>
                                    {selectedReg != undefined ?
                                        <div className={styles.fieldlist}>
                                            {selectedReg?.DATA?.map((field: any, index: number) => (
                                                <div className={styles.fielditem} key={index}>
                                                    <span className={styles.fieldlabel}>{field.TEXT}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>{field.answer}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    :
                                        <div>
                                            {t("selectedbreedingdog.results.loading")}
                                        </div>
                                    }
                                </div>}
                            ></Card>
                        </Col>
                        {selectedReg != undefined ?
                            <Col
                                sm="12"
                                md="12"
                                lg="6"
                                xl="6"
                                xxl="6"
                                className={styles.col}
                            >
                                <div className={styles.dogcardwrapper}>
                                    <div className={styles.dogavatar}>
                                        <span className={styles.dogavatarplaceholder}>
                                            {selectedReg?.DOGMATCH?.SEX === "male" ? "H" : "T"}
                                        </span>
                                        {selectedReg?.DOGMATCH?.ANIMAL_ID && (
                                            <img
                                                src={`/api/dogs/photo/${selectedReg.DOGMATCH.ANIMAL_ID}`}
                                                alt=""
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        )}
                                    </div>
                                <Card
                                    title={t("selectedbreedingdog.datamatch.title")}
                                    children={<div>
                                        <hr/>
                                        {selectedReg?.REGNUMBER != undefined ?
                                            <div className={styles.fieldlist}>
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("selectedbreedingdog.results.fields.dogname")}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>{selectedReg?.DOGMATCH?.NAME}</span>
                                                    </div>
                                                </div>
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("selectedbreedingdog.results.fields.regnum")}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>
                                                            <span
                                                                style={{ color: "#1D552B", cursor: "pointer", textDecoration: "underline" }}
                                                                onClick={() => window.open("/hunder/" + selectedReg?.DOGMATCH?.ANIMAL_ID, "_blank")}
                                                            >{selectedReg?.DOGMATCH?.REGNO}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>{t("selectedbreedingdog.results.fields.sex")}</span>
                                                    <div className={styles.fieldvaluerow}>
                                                        <span className={styles.fieldvalue}>{t("selectedbreedingdog.results.fields." + selectedReg?.DOGMATCH?.SEX)}</span>
                                                    </div>
                                                </div>
                                                {selectedReg?.DOGMATCH?.ADDRESS && (
                                                    <div className={styles.fielditem}>
                                                        <span className={styles.fieldlabel}>&nbsp;</span>
                                                        <div className={styles.fieldvaluerow}>
                                                            <span className={styles.fieldvalue}>{selectedReg?.DOGMATCH?.ADDRESS}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedReg?.DOGMATCH?.CITY && (
                                                    <div className={styles.fielditem}>
                                                        <span className={styles.fieldlabel}>&nbsp;</span>
                                                        <div className={styles.fieldvaluerow}>
                                                            <span className={styles.fieldvalue}>{selectedReg?.DOGMATCH?.CITY}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedReg?.DOGMATCH?.POSTAL_CODE && (
                                                    <div className={styles.fielditem}>
                                                        <span className={styles.fieldlabel}>{t("selectedbreedingdog.results.fields.postalcode")}</span>
                                                        <div className={styles.fieldvaluerow}>
                                                            <span className={styles.fieldvalue}>{selectedReg?.DOGMATCH?.POSTAL_CODE}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        : null}
                                        {selectedReg?.REGNUMBER != undefined && (
                                            <div style={{ marginTop: '15px' }}>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => {
                                                        if (window.confirm(t("selectedbreedingdog.removeconfirm"))) {
                                                            removeDogMatch();
                                                        }
                                                    }}
                                                >
                                                    {t("selectedbreedingdog.buttons.removematch")}
                                                </button>
                                            </div>
                                        )}
                                        {selectedReg?.REGNUMBER == undefined ?
                                            <div>
                                                <p>{t("selectedbreedingdog.datamatch.nomatch")}</p>
                                                <hr/>
                                                <div>
                                                    <input 
                                                        type="text" 
                                                        className="form-control"
                                                        placeholder={t("selectedbreedingdog.search.placeholder")}
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                searchDog();
                                                            }
                                                        }}
                                                    />
                                                    <button 
                                                        className="btn btn-primary" 
                                                        style={{ marginTop: '10px' }}
                                                        onClick={searchDog}
                                                        disabled={isSearching || !searchTerm}
                                                    >
                                                        {isSearching ? t("selectedbreedingdog.search.searching") : t("selectedbreedingdog.search.button")}
                                                    </button>
                                                </div>
                                                {searchResults.length > 0 && (
                                                    <div style={{ marginTop: '15px' }}>
                                                        {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: 0, marginBottom: '8px' }}>{t("shared.search.maxresults")}</p>}
                                                        <table className={styles.dogsearchtable} style={{ marginTop: '10px' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>{t("selectedbreedingdog.results.fields.regnum")}</th>
                                                                    <th>{t("selectedbreedingdog.results.fields.dogname")}</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {searchResults.map((dog: any, index: number) => (
                                                                    <tr key={index}>
                                                                        <td>{dog.REGNO}</td>
                                                                        <td>{dog.NAME}</td>
                                                                        <td>
                                                                            <button 
                                                                                className="btn btn-sm btn-primary"
                                                                                onClick={() => selectDog(dog)}
                                                                            >
                                                                                {t("selectedbreedingdog.search.select")}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: '8px', marginBottom: 0 }}>{t("shared.search.maxresults")}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        : null}
                                    </div>}
                                ></Card>
                                </div>
                            </Col>
                        : null}
                    </Row>
                </Container>
            </div>
        </div>
    );
};

class SelectedBreedingDogProps {
    
}

export default SelectedBreedingDog;